import { SearchOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, Input, InputNumber, List, Select, Typography } from 'antd';
import { GetOrganizationUnitDetailServicesQuery } from 'hooks/request-form';
import { requestform } from 'messages';
import React, { useEffect, useMemo, useState } from 'react';
import { DeadlineFilter, FindAllOrganizationUnitDto, OrganizationUnitDto, RequestData } from 'types';
import { useGetUserProfileQuery, useLazyGetOrganizationUnitsWithoutUserOrganizationUnitIdQuery } from '../../services';
import { TICKET_FILTER } from '../../utils';

const { Option } = Select;

const DEFAULT_PAGEINDEX = 1;
type TabKey = 'pending_reception' | 'in_progress' | 'collaboration' | 'completed' | 'obstacle';

interface RequestFilterFormInterface {
  setRequestData: React.Dispatch<React.SetStateAction<RequestData>>;
  requestData: RequestData;
  refreshData: () => void;
  clearFilterForm: () => void;
  setStoredFilters: React.Dispatch<React.SetStateAction<RequestData>>;
  isSendToCmis: boolean | null;
  setIsSendToCmis: React.Dispatch<React.SetStateAction<boolean | null>>;
  setIsShowCmisFilter: React.Dispatch<React.SetStateAction<boolean>>;

  isAssigned: boolean | null; // NEW
  setIsAssigned: React.Dispatch<React.SetStateAction<boolean | null>>; // NEW
  setIsShowAssignedFilter: React.Dispatch<React.SetStateAction<boolean>>; // NEW

  setSelectedTeam?: React.Dispatch<React.SetStateAction<OrganizationUnitDto | null>>;

  setApplyFilter?: (value: boolean) => void;
  activeTab: TabKey;
}

const RequestFilterForm: React.FC<RequestFilterFormInterface> = ({
  requestData,
  refreshData,
  clearFilterForm,
  setRequestData,
  setStoredFilters,
  isSendToCmis,
  setIsSendToCmis,
  setIsShowCmisFilter,
  setApplyFilter,
  activeTab,

  setSelectedTeam, // NEW

  isAssigned, // NEW
  setIsAssigned, // NEW
  setIsShowAssignedFilter // NEW
}) => {
  const { data: profile } = useGetUserProfileQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: organizationUnitChildrenDetail } = GetOrganizationUnitDetailServicesQuery(
    Number(profile?.data?.organizationUnitId || 0)
  );

  const [getUnits, { data, isFetching, isUninitialized, isError }] =
    useLazyGetOrganizationUnitsWithoutUserOrganizationUnitIdQuery();
  const [params, setParams] = useState<FindAllOrganizationUnitDto>({
    pageIndex: 1,
    pageSize: 50,
    isLoadAll: true
  });
  useEffect(() => {
    if (profile && profile?.data?.organizationUnit?.parent?.organizationUnitId) {
      setParams({
        ...params,
        parentId: [profile.data.organizationUnit.parent.organizationUnitId]
      });
    }
  }, [profile]);

  useEffect(() => {
    getUnits(params);
  }, [params, getUnits]);
  const [keyword, setKeyword] = useState('');
  const rows = data?.data.rows ?? [];
  const filtered = useMemo(
    () => rows.filter((u) => u.name?.toLowerCase().includes(keyword.toLowerCase())),
    [rows, keyword]
  );

  const [searchTerm, setSearchTerm] = useState<string>('');

  const [selectedServices, setSelectedServices] = useState<number[]>([]);
  const handleCheckboxChange = (serviceTypeId: number) => {
    setSelectedServices((prev) =>
      prev.includes(serviceTypeId) ? prev.filter((id) => id !== serviceTypeId) : [...prev, serviceTypeId]
    );
  };

  const [deadlineFilterType, setDeadlineFilterType] = useState<DeadlineFilter>(TICKET_FILTER.ALL);
  const [nearDeadlineMinutes, setNearDeadlineMinutes] = useState<number | null>(
    requestData?.nearDeadlineMinutes ?? TICKET_FILTER.DEFAULT_NEAR_DEADLINE_MINS
  );
  useEffect(() => {
    setDeadlineFilterType(requestData?.deadlineFilterType ?? TICKET_FILTER.ALL);
    setNearDeadlineMinutes(requestData?.nearDeadlineMinutes ?? TICKET_FILTER.DEFAULT_NEAR_DEADLINE_MINS);
  }, [requestData?.deadlineFilterType, requestData?.nearDeadlineMinutes]);

  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(requestData?.toTeamId ?? null);

  useEffect(() => {
    // nếu mở modal từ trạng thái đã có toTeamId
    setSelectedTeamId(requestData?.toTeamId ?? null);
  }, [requestData?.toTeamId]);

  // const canUsePriority = activeTab === 'in_progress' || activeTab === 'collaboration';
  const canUsePriority = activeTab === 'in_progress';
  const [priorityOnly, setPriorityOnly] = useState<boolean>(requestData?.isPriority === true);
  useEffect(() => {
    setPriorityOnly(requestData?.isPriority === true);
  }, [requestData?.isPriority, activeTab]);

  // ==> Apply Filter
  const handleCmisChange = (value: string) => {
    if (value === 'not_sent') setIsSendToCmis(false);
    else if (value === 'sent') setIsSendToCmis(true);
    else setIsSendToCmis(null); // 'all'

    // console.log('value: ', value);
    // console.log('isSendToCmis: ', isSendToCmis);
  };
  const handleAssignedChange = (value: string) => {
    if (value === 'assigned') setIsAssigned(true);
    else if (value === 'not_assigned') setIsAssigned(false);
    else setIsAssigned(null);
  };
  const handleApplyFilters = () => {
    const updatedFilters: RequestData = {
      ...requestData,
      pageIndex: DEFAULT_PAGEINDEX,
      serviceTypeId: selectedServices
    };

    if (isSendToCmis == null) {
      // bỏ hoàn toàn filter CMIS
      delete (updatedFilters as any).isSendToCmis;
      setIsSendToCmis(null);
      setIsShowCmisFilter(false);
    } else {
      updatedFilters.isSendToCmis = isSendToCmis;
      setIsShowCmisFilter(true);
    }

    // === Assigned ===
    if (isAssigned == null) {
      delete (updatedFilters as any).isAssigned;
      setIsAssigned(null);

      setIsShowAssignedFilter(false);
    } else {
      updatedFilters.isAssigned = isAssigned;
      setIsShowAssignedFilter(true);
    }

    // === toTeamId ===
    if (selectedTeamId == null) {
      delete (updatedFilters as any).toTeamId;
    } else {
      updatedFilters.toTeamId = selectedTeamId;
    }

    // === Deadline filters ===
    if (deadlineFilterType === TICKET_FILTER.ALL) {
      // KHÔNG gửi deadlineFilterType & nearDeadlineMinutes
      delete (updatedFilters as any).deadlineFilterType;
      delete (updatedFilters as any).nearDeadlineMinutes;
    } else if (deadlineFilterType === TICKET_FILTER.ALL_HAVE_DEADLINE) {
      updatedFilters.deadlineFilterType = TICKET_FILTER.ALL_HAVE_DEADLINE;
      delete (updatedFilters as any).nearDeadlineMinutes;
    } else if (deadlineFilterType === TICKET_FILTER.NEAR_DEADLINE) {
      updatedFilters.deadlineFilterType = TICKET_FILTER.NEAR_DEADLINE;
      updatedFilters.nearDeadlineMinutes = nearDeadlineMinutes || TICKET_FILTER.DEFAULT_NEAR_DEADLINE_MINS;
    } else if (deadlineFilterType === TICKET_FILTER.OVER_DEADLINE) {
      updatedFilters.deadlineFilterType = TICKET_FILTER.OVER_DEADLINE;
      delete (updatedFilters as any).nearDeadlineMinutes;
    }

    // --- PRIORITY (NEW) ---
    if (canUsePriority && priorityOnly) {
      updatedFilters.isPriority = true;
    } else {
      delete (updatedFilters as any).isPriority;
    }

    setRequestData(updatedFilters);
    setStoredFilters(updatedFilters);

    localStorage.setItem(`ticketFilter-${activeTab}`, JSON.stringify(updatedFilters)); // set local service type id array

    setApplyFilter?.(true);
    refreshData();
  };

  //  ACTION
  const actions: React.ReactNode[] = [
    <div className='flex h-full justify-between bg-[#F5F4F6] px-6'>
      <Button
        onClick={() => clearFilterForm()}
        className='border-none bg-transparent text-[#141414] underline shadow-none hover:text-[#141414]'
      >
        {requestform.deleteFilter}
      </Button>
      <Button onClick={handleApplyFilters} className='bg-[#1564E8] px-8 py-6 text-[#FFFFFF]'>
        {requestform.result}
      </Button>
    </div>
  ];
  return (
    <Card
      className='h-full w-full rounded-md bg-[#F5F4F6] text-center'
      title={requestform.filter}
      bordered={false}
      actions={actions}
    >
      <div className='h-[60vh] overflow-x-hidden overflow-y-scroll text-center'>
        {/**
                  ------------ Thông tin bộ filter  ------------
         */}

        {/*[1]: Danh sách phòng tổ*/}
        <section className='flex items-center justify-between space-x-8 border-b-2 py-6'>
          <Typography.Text className='text-[18px]/[24px] font-semibold text-[#141414]'>
            Danh sách phòng/tổ
          </Typography.Text>
          <Select
            showSearch
            style={{ width: 400 }}
            placeholder='Chọn phòng/tổ'
            value={selectedTeamId && selectedTeamId !== 0 ? selectedTeamId : undefined}
            allowClear
            onChange={(val) => {
              const id = (val as number) ?? null;
              setSelectedTeamId(id);

              if (setSelectedTeam) {
                const unit = rows.find((u) => u.organizationUnitId === id) ?? null;
                setSelectedTeam(unit);
              }
            }}
            onClear={() => {
              setSelectedTeamId(null);
              setSelectedTeam?.(null);
            }}
          >
            {filtered.map((u) => (
              <Option key={u.organizationUnitId} value={u.organizationUnitId}>
                {u.name}
              </Option>
            ))}
          </Select>
        </section>

        {/*[2]: Phiếu mới được cập nhật*/}
        <section className='flex items-center justify-between space-x-8 border-b-2 py-6'>
          <Typography.Text className='text-[18px]/[24px] font-semibold text-[#141414]'>
            Phiếu mới được cập nhật (số lần gọi)
          </Typography.Text>
          <div>
            <Select
              style={{ width: 400 }}
              value={priorityOnly ? 'priority' : 'all'}
              onChange={(val) => setPriorityOnly(val === 'priority')}
              disabled={!canUsePriority}
            >
              <Option value='all'>Mặc định</Option>
              <Option value='priority'>Phiếu mới được cập nhật</Option>
            </Select>
          </div>
        </section>

        {/*[3]: Thông tin chuyển CMIS*/}
        <section className='flex items-center justify-between space-x-8 border-b-2 py-6'>
          <Typography.Text className='text-[18px]/[24px] font-semibold text-[#141414]'>
            {requestform.isSendToCmisTitle}
          </Typography.Text>
          <Select defaultValue='all' onChange={handleCmisChange} style={{ width: 200 }}>
            <Option value='all'>Mặc định</Option>
            <Option value='not_sent'>Chưa chuyển CMIS</Option>
            <Option value='sent'>Đã chuyển CMIS</Option>
          </Select>
        </section>
        <section className='flex items-center justify-between space-x-8 border-b-2 py-6'>
          <Typography.Text className='text-[18px]/[24px] font-semibold text-[#141414]'>
            {requestform.isAssignedTitle}
          </Typography.Text>
          {/*<br />*/}
          <div>
            <Select defaultValue='all' onChange={handleAssignedChange} style={{ width: 200 }}>
              <Option value='all'>Mặc định</Option>
              <Option value='assigned'>Đã giao nhân viên</Option>
              <Option value='not_assigned'>Chưa giao nhân viên</Option>
            </Select>
          </div>
        </section>
        <section className='flex items-center justify-between space-x-8 border-b-2 py-6'>
          <Typography.Text className='text-[18px]/[24px] font-semibold text-[#141414]'>
            Thời hạn xử lý phiếu
          </Typography.Text>

          <div className='flex items-center gap-3'>
            <Select
              value={deadlineFilterType ?? TICKET_FILTER.ALL}
              onChange={(v) => setDeadlineFilterType(v)}
              style={{ width: 220 }}
              options={[
                { value: TICKET_FILTER.ALL, label: 'Mặc định' },
                { value: TICKET_FILTER.ALL_HAVE_DEADLINE, label: 'Phiếu có thời hạn xử lý' },
                { value: TICKET_FILTER.NEAR_DEADLINE, label: 'Phiếu sắp đến hạn' },
                { value: TICKET_FILTER.OVER_DEADLINE, label: 'Phiếu đã quá hạn' }
              ]}
            />
            <InputNumber
              max={100000}
              value={nearDeadlineMinutes}
              onChange={(v) => setNearDeadlineMinutes(Number(v) || null)}
              onBlur={() => {
                // nếu để trống => set mặc định 30
                if (nearDeadlineMinutes === null) setNearDeadlineMinutes(30);
              }}
              addonAfter='phút'
              style={{ width: 160 }}
              title='Số phút trước hạn để coi là sắp đến hạn'
              disabled={deadlineFilterType !== TICKET_FILTER.NEAR_DEADLINE} // Case "Gần hết hạn" => bật
            />
          </div>
        </section>
        <section className='pb-8 pt-8 text-left'>
          <Typography.Text className='text-[18px]/[24px] font-semibold text-[#141414]'>
            {requestform.typeOfService}
          </Typography.Text>

          <div className='pt-[20px]'>
            <Input
              className='h-[40px] w-full bg-white'
              placeholder={requestform.sortBy}
              suffix={<SearchOutlined className='text-[#B2B9C4]' />}
              variant='borderless'
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className='mt-2 space-y-4'>
              {organizationUnitChildrenDetail
                ?.filter((type) => type?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((type) => (
                  <List.Item key={type?.serviceTypeId} className='flex justify-between'>
                    <Checkbox
                      checked={selectedServices.includes(type?.serviceTypeId)}
                      onChange={() => handleCheckboxChange(type?.serviceTypeId)}
                    >
                      {type?.name}
                    </Checkbox>
                  </List.Item>
                ))}
              {organizationUnitChildrenDetail &&
                organizationUnitChildrenDetail.filter((type) =>
                  type?.name?.toLowerCase().includes(searchTerm.toLowerCase())
                ).length === 0 && <Typography.Text className='text-[#B2B9C4]'></Typography.Text>}
            </div>
          </div>
        </section>
      </div>
    </Card>
  );
};

export default RequestFilterForm;
